import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const SplashCosmic = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/onboarding-new');
    }, 3500);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center overflow-hidden relative"
      style={{ 
        background: 'radial-gradient(ellipse at center, #0f1419 0%, #050810 100%)'
      }}
    >
      {/* Cosmic nebulae background */}
      <div className="absolute inset-0">
        {/* Cyan nebula */}
        <motion.div
          className="absolute"
          style={{
            width: '600px',
            height: '600px',
            left: '10%',
            top: '20%',
            background: 'radial-gradient(circle, rgba(34, 211, 238, 0.15) 0%, rgba(34, 211, 238, 0.05) 40%, transparent 70%)',
            filter: 'blur(60px)',
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.6, 0.8, 0.6],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        
        {/* Purple nebula */}
        <motion.div
          className="absolute"
          style={{
            width: '500px',
            height: '500px',
            right: '15%',
            top: '30%',
            background: 'radial-gradient(circle, rgba(167, 139, 250, 0.18) 0%, rgba(139, 92, 246, 0.08) 40%, transparent 70%)',
            filter: 'blur(70px)',
          }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.5, 0.7, 0.5],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      {/* Constellation network */}
      <svg className="absolute inset-0" width="100%" height="100%">
        <defs>
          <linearGradient id="cosmic" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#A78BFA" stopOpacity="0.4" />
          </linearGradient>
          
          <filter id="cosmicGlow">
            <feGaussianBlur stdDeviation="2" result="blur"/>
            <feMerge>
              <feMergeNode in="blur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Network lines */}
        {[
          { x1: '20%', y1: '15%', x2: '35%', y2: '40%' },
          { x1: '35%', y1: '40%', x2: '50%', y2: '35%' },
          { x1: '50%', y1: '35%', x2: '65%', y2: '40%' },
          { x1: '65%', y1: '40%', x2: '80%', y2: '20%' },
          { x1: '20%', y1: '70%', x2: '35%', y2: '55%' },
          { x1: '35%', y1: '55%', x2: '50%', y2: '60%' },
          { x1: '50%', y1: '60%', x2: '65%', y2: '55%' },
          { x1: '65%', y1: '55%', x2: '80%', y2: '75%' },
        ].map((line, i) => (
          <motion.line
            key={i}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke="url(#cosmic)"
            strokeWidth="1"
            filter="url(#cosmicGlow)"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.4 }}
            transition={{ duration: 2, delay: 0.3 + i * 0.1 }}
          />
        ))}
        
        {/* Glowing nodes */}
        {[
          { cx: '20%', cy: '15%' }, { cx: '35%', cy: '40%' }, { cx: '50%', cy: '35%' },
          { cx: '65%', cy: '40%' }, { cx: '80%', cy: '20%' }, { cx: '20%', cy: '70%' },
          { cx: '35%', cy: '55%' }, { cx: '50%', cy: '60%' }, { cx: '65%', cy: '55%' },
          { cx: '80%', cy: '75%' },
        ].map((node, i) => (
          <motion.circle
            key={i}
            cx={node.cx}
            cy={node.cy}
            r="3"
            fill="#22D3EE"
            filter="url(#cosmicGlow)"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: [0, 1, 0.8],
              scale: [0, 1.5, 1],
            }}
            transition={{ duration: 1, delay: 0.5 + i * 0.08 }}
          />
        ))}
      </svg>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center">
        
        {/* Small stars above name - EXACT logo style */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <svg width="140" height="28" viewBox="0 0 140 28">
            <defs>
              <linearGradient id="starMini" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#22D3EE" />
                <stop offset="50%" stopColor="#818CF8" />
                <stop offset="100%" stopColor="#A78BFA" />
              </linearGradient>
            </defs>
            {[40, 70, 100].map((x, i) => (
              <g key={i}>
                {/* 4-pointed star */}
                <path
                  d={`M ${x} 4 L ${x+3} 10 L ${x+6} 14 L ${x+3} 18 L ${x} 24 L ${x-3} 18 L ${x-6} 14 L ${x-3} 10 Z`}
                  fill="url(#starMini)"
                  opacity="0.9"
                />
                {/* Connecting line */}
                {i < 2 && (
                  <line 
                    x1={x+6} 
                    y1="14" 
                    x2={x+24} 
                    y2="14" 
                    stroke="url(#starMini)" 
                    strokeWidth="1.5" 
                    opacity="0.4" 
                  />
                )}
              </g>
            ))}
          </svg>
        </motion.div>

        {/* App name - EXACT logo style (lowercase script) */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {/* Glow layer for visibility */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
              fontSize: '68px',
              fontWeight: 400,
              background: 'linear-gradient(90deg, #22D3EE, #818CF8, #A78BFA)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '4px',
              fontStyle: 'italic',
              filter: 'blur(30px)',
              opacity: 0.7,
              whiteSpace: 'nowrap',
            }}
          >
            taraanaya
          </div>
          
          {/* Main text - HIGH VISIBILITY */}
          <h1
            style={{
              fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
              fontSize: '68px',
              fontWeight: 400,
              background: 'linear-gradient(90deg, #22D3EE 0%, #60A5FA 30%, #818CF8 60%, #A78BFA 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '4px',
              textAlign: 'center',
              fontStyle: 'italic',
              filter: 'drop-shadow(0 0 40px rgba(34, 211, 238, 0.6)) drop-shadow(0 0 70px rgba(129, 140, 248, 0.4))',
              position: 'relative',
              whiteSpace: 'nowrap',
            }}
          >
            taraanaya
          </h1>
        </motion.div>

        {/* Subtitle - CLEARLY VISIBLE */}
        <motion.p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '16px',
            fontWeight: 600,
            color: '#E2E8F0',
            letterSpacing: '4px',
            textAlign: 'center',
            marginTop: '24px',
            textShadow: '0 2px 20px rgba(226, 232, 240, 0.5)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          DECISION INTELLIGENCE
        </motion.p>

        {/* Bottom tagline - HIGH CONTRAST */}
        <motion.p
          className="absolute bottom-16"
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '11px',
            fontWeight: 500,
            color: '#CBD5E0',
            letterSpacing: '2.5px',
            textAlign: 'center',
            textShadow: '0 1px 10px rgba(203, 213, 224, 0.3)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.9 }}
          transition={{ duration: 0.8, delay: 1.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          DECISION INTELLIGENCE PLATFORM POWERED BY ASTROLOGY
        </motion.p>
      </div>

      {/* Floating particles */}
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: Math.random() * 3 + 1,
            height: Math.random() * 3 + 1,
            background: i % 2 === 0 ? '#22D3EE' : '#A78BFA',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            opacity: 0.3,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.2, 0.6, 0.2],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
};

export default SplashCosmic;
